/* eslint-disable max-lines */

import { SavedSearch, Team } from '@litlingo/client';
import { reviewSetCreated } from 'actions/envelopeListView';
import Modal from 'components/Modal';
import TeamSelectRedesign from 'components/TeamsManager/TeamSelectRedesign';

import { createReviewSetSets, deleteSavedSearch } from 'actions';
import { TAG_ADD_ICON, TAG_REMOVE_ICON } from 'constants/filterIcons';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import useTree from 'hooks/envelopes/useTree';
import useAllTeams from 'hooks/teams/useAllTeams';
import { cloneDeep } from 'lodash';
import pluralize from 'pluralize';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getUser } from 'selectors/auth';
import { useSelector } from 'store';
import { RouteParams } from 'types';
import {
  buildFSFromParams,
  fillDefaultTree,
  getNavParamsFromFilters,
  Operator,
  setNewValue,
  transformToString,
  Tree,
} from 'utils/parserTree';
import { getParamsFromUrl } from 'utils/urls';
import { v4 } from 'uuid';
import ReviewSetsCollapsableTeamSection from './ReviewSetsCollapsableTeamSection';

type ComponentProps = {
  reviewSet: SavedSearch | null;
  isModalOpen: boolean;
  toggleModalOpen: () => void;
};

type Set = {
  teamId: string;
  regionId: string;
  teamName: string;
  regionName: string;
  selected: boolean;
  setUuid?: string;
};

const ReviewSetSetsModal: React.FC<ComponentProps> = ({
  reviewSet,
  isModalOpen,
  toggleModalOpen,
}) => {
  const dispatch = useDispatch();

  const user = useSelector(getUser);

  const [originalTeams, setOriginalTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [originalRegions, setOriginalRegions] = useState<Team[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<Team[]>([]);

  const [selectedSets, setSelectedSets] = useState<Set[]>([]);

  const { data: teamsData, isLoading } = useAllTeams();

  let navParams: RouteParams = {};

  if (user.customer) {
    if (reviewSet) {
      navParams = getParamsFromUrl(
        reviewSet.url,
        resourceQueryParamName.envelopes,
        'envelope-list',
        user.customer?.config
      );
    }
  }
  const filterSearch = (navParams.filters_search ?? buildFSFromParams(navParams)) as string;
  const { data, error } = useTree(filterSearch);

  let tree: Tree | null = {
    op: Operator.AND,
    data: [],
    id: v4(),
  };
  if (data && data.data.length > 0 && !error) {
    navParams = getNavParamsFromFilters(filterSearch, navParams);
    tree = fillDefaultTree(data, navParams);
  }

  useEffect(() => {
    const sets: Set[] = [];
    if (selectedTeams.length > 0 && selectedRegions.length > 0) {
      selectedTeams.forEach((team) => {
        selectedRegions.forEach((region) => {
          const selectedSet = selectedSets.find(
            (s) => s.regionId === region.uuid && s.teamId === team.uuid
          );

          sets.push({
            teamId: team.uuid,
            teamName: team.name,
            regionId: region.uuid,
            regionName: region.name,
            selected: selectedSet ? selectedSet.selected : true,
          });
        });
      });
    } else if (selectedTeams.length > 0) {
      selectedTeams.forEach((team) => {
        const selectedSet = selectedSets.find((s) => !s.regionId && s.teamId === team.uuid);
        sets.push({
          teamId: team.uuid,
          teamName: team.name,
          regionId: '',
          regionName: '',
          selected: selectedSet ? selectedSet.selected : true,
        });
      });
    } else if (selectedRegions.length > 0) {
      selectedRegions.forEach((region) => {
        const selectedSet = selectedSets.find((s) => !s.teamId && s.regionId === region.uuid);
        sets.push({
          teamId: '',
          teamName: '',
          regionId: region.uuid,
          regionName: region.name,
          selected: selectedSet ? selectedSet.selected : true,
        });
      });
    }

    setSelectedSets(sets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeams, selectedRegions]);

  useEffect(() => {
    const sets: Set[] = [];
    const teams: Team[] = [];
    const regions: Team[] = [];

    if (reviewSet?.children && reviewSet?.children?.length > 0) {
      reviewSet.children.forEach((child) => {
        // @ts-ignore
        const team = teamsData?.records.find((t) => t.uuid === child.config.teamId);
        // @ts-ignore
        const region = teamsData?.records.find((t) => t.uuid === child.config.regionId);

        if (team || region) {
          sets.push({
            teamId: team?.uuid || '',
            teamName: team?.name || '',
            regionId: region?.uuid || '',
            regionName: region?.name || '',
            selected: true,
          });

          if (team && teams.every((t) => t.uuid !== team.uuid)) {
            teams.push(team);
          }
          if (region && regions.every((r) => r.uuid !== region.uuid)) {
            regions.push(region);
          }
        }
      });

      setSelectedSets(sets);

      setOriginalTeams(teams);
      setSelectedTeams(teams);

      setOriginalRegions(regions);
      setSelectedRegions(regions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handleAddTeam = (teams: Team[]): void => {
    setSelectedTeams((t) => [...t, ...teams]);
  };

  const handleRemoveTeam = (teams: Team[]): void => {
    setSelectedTeams((ts) => ts.filter((t) => teams.find((te) => te.uuid !== t.uuid)));
  };

  const handleAddRegion = (regions: Team[]): void => {
    setSelectedRegions((r) => [...r, ...regions]);
  };

  const handleRemoveRegion = (regions: Team[]): void => {
    setSelectedRegions((rs) => rs.filter((r) => regions.find((re) => re.uuid !== r.uuid)));
  };

  const handleSave = (): void => {
    if (!reviewSet) return;

    const setsToCreate: Partial<SavedSearch>[] = [];
    const deletedTeams = originalTeams.filter(
      (ot) => !selectedTeams.find((st) => st.uuid === ot.uuid)
    );
    const deletedRegions = originalRegions.filter(
      (or) => !selectedRegions.find((sr) => sr.uuid === or.uuid)
    );

    selectedSets
      .filter((s) => !s.selected)
      .forEach((set) => {
        const foundChild = reviewSet.children?.find(
          // @ts-ignore
          (child) => child.config.teamId === set.teamId && child.config.regionId === set.regionId
        );

        if (foundChild) {
          dispatch(deleteSavedSearch({ savedSearch: foundChild }));
        }
      });

    reviewSet.children?.forEach((child) => {
      if (
        // @ts-ignore
        deletedTeams.find((t) => t.uuid === child.config?.teamId) ||
        // @ts-ignore
        deletedRegions.find((r) => r.uuid === child.config?.regionId)
      ) {
        dispatch(deleteSavedSearch({ savedSearch: child }));
      }
    });

    selectedSets
      .filter((s) => s.selected)
      .filter(
        (set) =>
          !reviewSet.children?.find(
            // @ts-ignore
            (child) => child.config?.teamId === set.teamId && child.config.regionId === set.regionId
          )
      )
      .forEach((set) => {
        if (tree) {
          let newTree = cloneDeep(tree);
          let name = '';

          if (set.regionId) {
            if (set.teamId) {
              name = `${set.regionName} (${set.teamName})`;
            } else {
              name = `${set.regionName}`;
            }
          } else if (set.teamId) {
            name = `${set.teamName}`;
          }

          if (set.regionId) {
            newTree = setNewValue(newTree, 'sender_regions', set.regionId);
          }
          if (set.teamId) {
            newTree = setNewValue(newTree, 'sender_team_uuids', set.teamId);
          }

          const urlQuery = `envelopes__filters_search=${transformToString(newTree)}`;

          const newSet = {
            name,
            description: '',
            private: reviewSet.private,
            // @ts-ignore
            parent_uuid: reviewSet.uuid,

            url: urlQuery,

            is_starred: reviewSet.is_starred,
            param_count: reviewSet.param_count + 2,
            is_assignable: reviewSet.is_assignable,
            priority: reviewSet.priority,
            config: { ...reviewSet.config, teamId: set.teamId, regionId: set.regionId },
            display_in_assignments: reviewSet.display_in_assignments,

            scope: 'all',

            sample_size: reviewSet.sample_size,
            based_on: reviewSet.sample_based_on || '',
            cadence: reviewSet.sample_cadence || '',

            // @ts-ignore
            tenant_name: reviewSet.tenant_name,
          };

          // @ts-ignore
          setsToCreate.push(newSet);
        }
      });

    if (setsToCreate.length > 0) {
      dispatch(
        createReviewSetSets({
          parentId: reviewSet.uuid,
          parentName: reviewSet.name,
          reviewSets: setsToCreate,
        })
      );
    }

    toggleModalOpen();
    dispatch(reviewSetCreated(false));
  };

  const handleCancel = (): void => {
    toggleModalOpen();
    dispatch(reviewSetCreated(false));
  };

  const toggleSetSelection = (set: Set): void => {
    setSelectedSets((sets) =>
      sets.map((s) =>
        s.regionId === set.regionId && s.teamId === set.teamId ? { ...s, selected: !s.selected } : s
      )
    );
  };

  const teamSetIds = [...new Set(selectedSets.map((set) => set.teamId))];

  const renderSecondaryComponent = (
    <span className="whitespace-no-wrap self-end">
      {selectedSets.filter((s) => s.selected).length}{' '}
      {pluralize('groupings', selectedSets.filter((s) => s.selected).length)} total
    </span>
  );

  const renderSetsOptions = (
    <div className="h-104 pb-2 flex flex-col justify-between">
      <div className="h-full flex flex-col gap-2">
        <div className="">Add the areas of breakout and select the variants needed</div>
        <div className="flex flex-row gap-4">
          <div className="flex flex-col gap-1">
            <span>Region</span>
            <div className="w-44">
              <TeamSelectRedesign
                includedTeams={selectedRegions}
                addIncludedTeams={handleAddRegion}
                removeIncludedTeams={handleRemoveRegion}
                kind="region"
                showLabels
                labelLayout="vertical"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span>Division</span>
            <div className="w-44">
              <TeamSelectRedesign
                includedTeams={selectedTeams}
                addIncludedTeams={handleAddTeam}
                removeIncludedTeams={handleRemoveTeam}
                kind="division"
                showLabels
                labelLayout="vertical"
              />
            </div>
          </div>
        </div>
        <div className="h-full mt-8 flex flex-col gap-2 overflow-auto">
          <span>Assignment Groupings</span>
          <div className="h-full flex flex-col gap-3 overflow-auto custom-scrollbar">
            {selectedTeams.length > 0
              ? teamSetIds.map((teamSetId) => (
                  <ReviewSetsCollapsableTeamSection
                    key={teamSetId}
                    sets={selectedSets.filter((set) => set.teamId === teamSetId)}
                    toggleSetSelection={toggleSetSelection}
                  />
                ))
              : selectedSets.length > 0 && (
                  <div className="flex flex-row gap-1">
                    {selectedSets.map((set) => (
                      <div
                        key={`${set.teamId}-${set.regionId}`}
                        className={`px-1 py-0.5 flex flex-row gap-1 rounded text-small ${
                          set.selected ? 'bg-litlingo-success-light' : 'bg-litlingo-gray-0.5'
                        }`}
                      >
                        <span>{set.regionName}</span>
                        <button
                          onClick={(e): void => {
                            e.stopPropagation();
                            toggleSetSelection(set);
                          }}
                          className="focus:outline-none outline-none"
                          type="button"
                        >
                          {set.selected ? TAG_REMOVE_ICON : TAG_ADD_ICON}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isModalOpen && (
        <Modal
          title={`Review sets for ${reviewSet?.name}`}
          body={renderSetsOptions}
          cancelButton
          cancelButtonText="Cancel"
          cancelButtonOnclick={handleCancel}
          okButton
          okButtonText="Save"
          okButtonOnClick={handleSave}
          toggleShowModal={toggleModalOpen}
          secondaryComponent={renderSecondaryComponent}
          style={{
            width: '50rem',
          }}
        />
      )}
    </>
  );
};

export default ReviewSetSetsModal;
